import React from 'react';
import SkeletonListing from '../skeleton/SkeletonListing';
import configService from '../../services/config.service';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { formatDate, formatNumber } from '../../utils/Helper';
import ImageLoader from './ImageLoader';
import { useNavigate } from 'react-router-dom';
import { HiSortAscending } from 'react-icons/hi';
import { PiSortAscendingBold } from 'react-icons/pi';
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import humanizeDuration from 'humanize-duration';
import { DownloadIcon } from './IconProvider';

const TableComponent = ({
  headerList,
  headerSlug,
  isListLoader,
  columnCount,
  pagination,
  setPagination,
  isSortingLoader,
  rowCount,
  filterData,
  isList,
  fetchData,
  slug,
}) => {
  const { user: _authUser } = useSelector((state) => state.auth);
  const { t } = useTranslation(['projectListingRootComponent', 'common']);

  //React Table
  const [sorting, setSorting] = useState([]);
  const data = useMemo(() => isList || [], [isList]);
  const navigate = useNavigate();
  const pageNavigate = (project_id) => {
    navigate('/owned-assets/details/' + project_id);
    return false;
  };

  let columns;
  if (slug === 'isFiatList') {
    columns = [
      {
        header: '#',
        accessorKey: 'project_id',
      },
      {
        header: headerList[0]?.Name,
        accessorKey: 'name',
        enableSorting: false,
        cell: ({ row }) => {
          const projectDetail = row.original.projectDetail;
          const projectName = projectDetail?.name;
          const projectIcon = projectDetail?.ProjectsType?.icon_url;

          return (
            <div className="flex items-center font-semibold text-md">
              {projectIcon && (
                <ImageLoader src={projectIcon} className="mr-2" alt="" />
              )}
              {projectName ?? 'N/A'}
            </div>
          );
        },
      },
      {
        header: headerList[1]?.Name,
        accessorKey: 'type',
        enableSorting: false,
        cell: ({ row }) => {
          const type = row.original.type;
          // return <>{type ?? 'N/A'}</>;
          return (
            <span
              className={
                'bg-[#85919F] py-[2px] px-3 rounded-md text-white font-semibold inline-block uppercase text-xs '
              }
            >
              {type ?? 'N/A'}
            </span>
          );
        },
      },

      {
        header: headerList[2]?.Name,
        accessorKey: 'transactions_id',
        enableSorting: false,
        cell: ({ row }) => {
          const transactionId = row.original.transactions_id;
          return <>{transactionId ?? 'N/A'}</>;
        },
      },
      {
        header: headerList[3]?.Name,
        accessorKey: 'quantity',
        cell: ({ row }) => {
          const quantity = row.original.quantity;
          return <>{quantity ?? '0'}</>;
        },
      },
      {
        header: headerList[4]?.Name,
        accessorKey: 'total_amount',
        cell: ({ row }) => {
          const totalAmt = row.original.total_amount ?? 0;
          return <span>{formatNumber(totalAmt)}</span>;
        },
      },
      {
        header: headerList[5]?.Name,
        accessorKey: 'created_at',
        cell: ({ row }) => {
          const createdAt = row.original.created_at;
          return (
            <>
              {createdAt ? formatDate(createdAt, 'DD MMM YYYY HH:mm') : 'N/A'}
            </>
          );
        },
      },
      {
        header: headerList[6]?.Name,
        accessorKey: 'payment_method',
        cell: ({ row }) => {
          const paymentAmt = Number(row.original?.amount);
          const voucherAmt = Number(
            row.original?.voucherUtilize?.discount_amount,
          );

          return (
            <>
              {paymentAmt > 0 && voucherAmt > 0 ? (
                <>
                  <span className="font-medium">
                    {row.original?.payment_method +
                      ': ' +
                      formatNumber(paymentAmt)}
                  </span>
                  <span className="block font-medium">
                    {'voucher: ' + formatNumber(voucherAmt)}
                  </span>
                </>
              ) : (
                <span className="font-medium">
                  {row.original.payment_method ?? 'N/A'}
                </span>
              )}
            </>
          );
        },
      },

      {
        header: headerList[7]?.Name,
        accessorKey: 'status',
        cell: ({ row }) => {
          const value = row.original.status;

          const statusMapping = {
            pending: { className: 'bg-[#F49030]' },
            completed: { className: 'bg-[#95D72F]' },
            failed: { className: 'bg-[#969696]' },
            created: { className: 'bg-[#394DBB]' },
            expired: { className: 'bg-gray-500' },
            canceled: { className: 'bg-gray-500' },
            open: { className: 'bg-gray-500' },
          };

          const statusInfo = statusMapping[value] || {
            className: 'bg-gray-500',
          };
          return (
            <span
              className={`px-2.5 py-1 rounded-md text-white font-semibold inline-block uppercase text-xs ${statusInfo.className}`}
            >
              {t(`common:${value}`)}
            </span>
          );
        },
      },
      {
        header: headerList[8]?.Name,
        accessorKey: 'action',
        enableSorting: false,
        cell: ({ row }) => {
          const pdfUrl = row.original.pdf_url;
          const tooltipId = `download_tooltip_${row.id}`;

          return (
            <div className="relative inline-block">
              {pdfUrl && (
                <>
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-tooltip-id={tooltipId}
                    className="border border-gray-300 px-3 py-1.5 bg-white rounded-md text-[#0B243F] flex items-center gap-2"
                  >
                    <DownloadIcon />
                  </a>
                  <ReactTooltip
                    id={tooltipId}
                    place="top"
                    content="Purchase Order"
                  />
                </>
              )}
            </div>
          );
        },
      },
    ];
  } else if (slug === 'onChainList') {
    columns = [
      {
        header: '#',
        accessorKey: 'project_id',
      },
      {
        header: headerList[0]?.Name,
        accessorKey: 'name',
        enableSorting: false,
        cell: ({ row }) => {
          const projectName = row.original.projectDetail?.name || 'N/A';
          const iconUrl = row.original.projectDetail?.ProjectsType?.icon_url;
          const contractAddress = row.original.projectDetail?.contract_address;
          const explorerUrl = row.original.projectDetail?.tokensExplorerUrl;

          return (
            <div>
              <div className="flex items-center font-semibold text-md">
                <ImageLoader src={iconUrl} className="mr-2" alt="" />

                <p className="truncate font-medium">{projectName}</p>
              </div>

              {/* Contract Address with Link */}
              {contractAddress && (
                <div className="break-all w-28 text-BlueText mt-1">
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={explorerUrl || ''}
                    className="flex items-center"
                  >
                    <img
                      src="/img/linkdirection-icon.svg"
                      className="mr-2"
                      alt="Link Icon"
                    />
                    <div className="w-24 overflow-ellipsis whitespace-nowrap overflow-hidden">
                      {configService
                        .shortenAddress(contractAddress)
                        .toUpperCase()}
                    </div>
                  </a>
                </div>
              )}
            </div>
          );
        },
      },
      {
        header: headerList[1]?.Name,
        accessorKey: 'hash',
        enableSorting: false,
        cell: ({ row }) => {
          const txnHashURL = row.original.txnHashURL || '';
          const transactionsHash = row.original.hash;

          return (
            <div className="break-all w-28 text-BlueText">
              {transactionsHash ? (
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={txnHashURL}
                  className="flex items-center"
                >
                  <img
                    src="/img/linkdirection-icon.svg"
                    className="mr-2"
                    alt="Link Icon"
                  />
                  <div className="w-24 overflow-ellipsis whitespace-nowrap overflow-hidden">
                    {configService
                      .shortenAddress(transactionsHash)
                      .toUpperCase()}
                  </div>
                </a>
              ) : (
                'NA'
              )}
            </div>
          );
        },
      },
      {
        header: headerList[2]?.Name,
        accessorKey: 'full_name',
        enableSorting: false,
        cell: ({ row, index }) => {
          const fromUser = row.original.fromUser;
          const fromWalletAddress = row.original.from_wallet_address;

          return (
            <div>
              <p
                data-tooltip-id={`from_wallet_address${index}`}
                className="break-all w-24 truncate text-BlueText"
              >
                {fromUser?.id === 1 ? 'Issuer' : fromUser?.full_name || 'NA'}
              </p>
              <ReactTooltip
                id={`from_wallet_address${index}`}
                place="bottom"
                content={fromWalletAddress}
              />
            </div>
          );
        },
      },
      {
        header: headerList[3]?.Name, // Dynamically set the header name
        accessorKey: 'to_wallet_address', // Key to access the 'to_wallet_address' field in the row
        enableSorting: false,
        cell: ({ row, index }) => {
          const toWalletAddress = row.original.to_wallet_address;

          return (
            <div>
              <p
                data-tooltip-id={`to_wallet_address${index}`}
                className="break-all w-24 truncate text-BlueText"
              >
                {toWalletAddress
                  ? configService.shortenAddress(toWalletAddress).toUpperCase()
                  : 'NA'}
              </p>
              <ReactTooltip
                id={`to_wallet_address${index}`}
                place="bottom"
                content={toWalletAddress || 'NA'}
              />
            </div>
          );
        },
      },
      {
        header: headerList[4]?.Name,
        accessorKey: 'quantity',
      },

      {
        header: headerList[5]?.Name,
        accessorKey: 'created_at',
        cell: ({ row }) => {
          const createdAt = row.original.created_at;
          return (
            <>
              {createdAt ? formatDate(createdAt, 'DD MMM YYYY HH:mm') : 'N/A'}
            </>
          );
        },
      },
      {
        header: headerList[6]?.Name,
        accessorKey: 'status',
        cell: ({ row }) => {
          const statuss = row.original.status;

          const statusClasses =
            statuss === 0
              ? 'bg-[#3D4CB2] text-white'
              : 'bg-[#A7D760] text-[#0B243F]';

          return (
            <span
              className={`px-2.5 py-1 rounded-md font-semibold inline-block uppercase text-xs ${statusClasses}`}
            >
              {statuss === 1 ? 'Transferred' : 'Pending'}{' '}
            </span>
          );
        },
      },
    ];
  } else if (slug === 'detailsPageOnChainList') {
    columns = [
      {
        header: '#',
        accessorKey: 'id',
      },
      {
        header: headerList[0]?.Name,
        accessorKey: 'created_at',
        cell: ({ row }) => {
          const createdAt = row.original.created_at;
          return (
            <>
              {createdAt ? formatDate(createdAt, 'DD MMM YYYY HH:mm') : 'N/A'}
            </>
          );
        },
      },
      {
        header: headerList[1]?.Name,
        accessorKey: 'type',
        cell: ({ row }) => {
          const projectType = row.original.type || 'N/A';
          return (
            <span className="bg-[#85919F] px-2.5 py-1 rounded-md text-white font-semibold inline-block uppercase text-xs">
              {projectType}
            </span>
          );
        },
      },
      {
        header: headerList[2]?.Name,
        accessorKey: 'from_wallet_address',
        enableSorting: false,
        cell: ({ row, index }) => {
          const fromUser = row.original.fromUser;
          const fromWalletAddress = row.original.from_wallet_address;

          return (
            <div>
              <div
                data-tooltip-id={`from_wallet_address${index}`}
                className="text-[#3D4CB2]"
              >
                {fromUser?.id === 1 ? 'Issuer' : fromUser?.full_name || 'NA'}
              </div>
              {_authUser.userId === row.original.from_user_id && (
                <span className="table-font-bg-border">You</span>
              )}
              <ReactTooltip
                id={`from_wallet_address${index}`}
                place="bottom"
                content={fromWalletAddress || 'NA'}
              />
            </div>
          );
        },
      },
      {
        header: headerList[3]?.Name,
        accessorKey: 'to_wallet_address',
        enableSorting: false,
        cell: ({ row, index }) => {
          const toWalletAddress = row.original.to_wallet_address;
          const toUserId = row.original.to_user_id;

          return (
            <div>
              <span
                data-tooltip-id={`to_wallet_address${index}`}
                className="text-[#3D4CB2] inline-block"
              >
                {toWalletAddress
                  ? configService.shortenAddress(toWalletAddress).toUpperCase()
                  : 'NA'}
              </span>{' '}
              {_authUser.userId === toUserId && (
                <span className="table-font-bg-border">You</span>
              )}
              <ReactTooltip
                id={`to_wallet_address${index}`}
                place="bottom"
                content={toWalletAddress || 'NA'}
              />
            </div>
          );
        },
      },
      {
        header: headerList[4]?.Name,
        accessorKey: 'quantity',
        cell: ({ row }) => {
          const quantity = row.original.quantity;

          return (
            <div className="px-6 py-4 font-semibold text-[#0B243F] dark:text-slate-200">
              {quantity || '0'}{' '}
            </div>
          );
        },
      },
      {
        header: headerList[5]?.Name,
        accessorKey: 'status',
        cell: ({ row }) => {
          const status = row.original.status;

          return (
            <div>
              <span
                className={`px-2.5 py-1 rounded-md font-semibold inline-block uppercase text-xs ${
                  status === 0
                    ? 'bg-[#3D4CB2] text-white'
                    : 'text-[#0B243F] bg-[#A7D760]'
                }`}
              >
                {status === 1 ? 'Transferred' : 'Pending'}{' '}
              </span>
            </div>
          );
        },
      },
      {
        header: headerList[6]?.Name,
        accessorKey: 'hash',
        enableSorting: false,
        cell: ({ row }) => {
          const txnHash = row.original.hash;
          const txnHashURL = row.original.txnHashURL;

          return (
            <div>
              <div className="flex text-[#3D4CB2] font-semibold uppercase text-xs">
                <Link
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center"
                  to={txnHashURL || ''}
                >
                  <img src="/img/send-icon.svg" className="mr-2" alt="icon" />
                  {txnHash
                    ? configService.shortenAddress(txnHash).toUpperCase()
                    : 'NA'}{' '}
                </Link>
              </div>
            </div>
          );
        },
      },
    ];
  } else if (slug === 'payoutList') {
    columns = [
      {
        header: '#',
        accessorKey: 'id',
      },
      {
        header: headerList[0]?.Name,
        accessorKey: 'status',
        cell: ({ row }) => {
          const status = row.original.status;
          return (
            <span
              className={`px-2.5 py-1 rounded-md font-semibold inline-block uppercase text-xs
          ${
            status === 0
              ? 'bg-[#3D4CB2] text-white'
              : 'bg-[#A7D760] text-[#0B243F]'
          }
        `}
            >
              {status === 1 ? 'Paid' : 'Pending'}
            </span>
          );
        },
      },
      {
        header: headerList[1]?.Name,
        accessorKey: 'payout_amount',
        cell: ({ row }) => {
          const payoutAmount = row.original?.payout_amount;
          return (
            <div className="text-[#0B243F] dark:text-slate-200">
              {formatNumber(payoutAmount)}
            </div>
          );
        },
      },
      {
        header: headerList[2]?.Name,
        accessorKey: 'projectPayout.from_period',
        enableSorting: false,
        cell: ({ row }) => {
          const { from_period, to_period } = row.original?.projectPayout || {};
          return (
            <span className="text-[#0B243F] inline-block dark:text-slate-200">
              {from_period ? formatDate(from_period, 'DD MMM YYYY') : 'NA'} -{' '}
              {to_period ? formatDate(to_period, 'DD MMM YYYY') : 'NA'}
            </span>
          );
        },
      },
    ];
  } else if (slug === 'projecListing') {
    columns = [
      {
        header: t('project_name'),
        accessorKey: 'name',
        enableSorting: headerSlug === 'owned-assets' ? false : true,
        cell: ({ row }) => {
          const projectName =
            headerSlug === 'owned-assets'
              ? row.original.projectDetail.name
              : row.original.name;
          const isRepaid = row.original.is_repaid;

          return (
            <>
              <div className="flex flex-col">
                <span className="text-sm font-medium">{projectName}</span>
                {isRepaid && (
                  <span className="mt-0.5 px-1 rounded-sm text-[12px]  text-white font-medium bg-[#97c05b] w-fit">
                    {t('common:repaid')}
                  </span>
                )}
              </div>
            </>
          );
        },
      },
      {
        header: t('image'),
        accessorKey: 'project_image',
        enableSorting: false,
        cell: ({ row }) => (
          <img
            src={
              headerSlug === 'owned-assets'
                ? row.original?.projectDetail?.project_image ||
                  '/img/project_image.png'
                : row.original?.project_image || '/img/project_image.png'
            }
            alt="Project"
            style={{ width: '50px', height: '50px' }}
          />
        ),
      },
      {
        header:
          headerSlug === 'owned-assets'
            ? t('owned')
            : t('common:total_offering'),
        accessorKey: 'total_quantity',
        enableSorting: false,
        cell: ({ row }) =>
          headerSlug === 'owned-assets'
            ? row?.original?.total_quantity || '0'
            : formatNumber(
                Number(
                  row?.original?.total_minted * row?.original?.nominal_value,
                ),
              ),
      },
      {
        header:
          headerSlug === 'owned-assets'
            ? t('common:invested')
            : t('common:nominal_value'),
        accessorKey: 'total_invested_amount',
        enableSorting: headerSlug === 'owned-assets' ? true : false,
        cell: ({ row }) => {
          const project = row.original;

          if (headerSlug === 'owned-assets') {
            const totalInvestedAmount = project?.total_invested_amount;
            const formattedAmount = totalInvestedAmount
              ? formatNumber(totalInvestedAmount, 'en-DE', 'EUR', '0')
              : '0';
            return <>{formattedAmount}</>;
          } else {
            return <>{formatNumber(project?.nominal_value)} </>;
          }
        },
      },
      {
        header:
          headerSlug === 'owned-assets'
            ? t('common:interest_rate')
            : t('dashboard:capacity'),
        accessorKey: 'quantity',
        enableSorting: false,
        cell: ({ row }) => {
          if (headerSlug === 'owned-assets') {
            return row?.original?.projectDetail?.investment_type === 'bond'
              ? row?.original?.projectDetail?.interest_rate + ' %' || '0.00'
              : 'NA';
          } else {
            const energyCapacity = row?.original?.energy_capacity || 'NA';
            return energyCapacity + ' MW';
          }
        },
      },
      {
        header:
          headerSlug === 'owned-assets'
            ? 'E. APY'
            : t('dashboard:expected_return'),
        accessorKey: 'projectDetail.expected_apy',
        enableSorting: false,
        cell: ({ row }) => {
          const value =
            headerSlug === 'owned-assets'
              ? row?.original?.projectDetail?.expected_apy
              : row?.original?.expected_apy;

          return (
            formatNumber(value, {
              style: 'decimal',
              decimalPlaces: 2,
            }) || 'NA'
          );
        },
      },
      {
        header: t('common:maturity_date'),
        accessorKey: 'projectDetail.maturity_date',
        enableSorting: false,
        cell: ({ row }) => {
          const maturityDate = row.original.projectDetail?.maturity_date;
          return maturityDate ? formatDate(maturityDate, 'DD MMM YYYY') : 'NA';
        },
      },
      {
        header: t(headerSlug === 'owned-assets' ? t('common:lifespan') : 'NA'),
        accessorKey: 'projectDetail.life_span_to',
        enableSorting: false,
        cell: ({ row }) => {
          const project = row.original;
          const lifeSpanTo = project?.projectDetail?.life_span_to;

          if (!lifeSpanTo) {
            return 'NA';
          }

          const now = DateTime.local();
          const end = DateTime.fromISO(lifeSpanTo);

          if (now > end) {
            return t('common:ended');
          }

          const diff = end.diff(now).as('milliseconds');
          const humanizedDiff = humanizeDuration(diff, {
            language: t('common:language_code'),
            largest: 2,
            round: true,
            conjunction: t('common:conjunction'),
          });

          return humanizedDiff;
        },
      },
    ];
  }
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualSorting: true,
    state: {
      sorting: sorting
        ? [{ id: sorting.field, desc: sorting.order === 'desc' }]
        : [],
      pagination: {
        pageIndex: pagination.currentPage - 1,
        pageSize: filterData.perPage,
      },
    },
    onSortingChange: () => {},
  });
  const handlePageChange = (pageIndex) => {
    setPagination((prev) => {
      const updatedPagination = { ...prev, currentPage: pageIndex + 1 };
      fetchData(updatedPagination, sorting);
      return updatedPagination;
    });
  };
  const handleSorting = (field) => {
    setSorting((currentSorting) => {
      const isCurrentAsc =
        currentSorting.field === field && currentSorting.order === 'asc';
      const newOrder = isCurrentAsc ? 'desc' : 'asc';
      const updatedSorting = { field, order: newOrder };

      fetchData({ currentPage: 1 }, updatedSorting, true);
      return updatedSorting;
    });
  };

  return (
    <>
      <table className="tableradus w-full text-sm text-left  bg-[#F3F3F3] px-3 dark:bg-slate-800 dark:text-slate-200 border-separate border-spacing-y-3 shadow-none">
        <thead className="text-xs uppercase">
          {table?.getHeaderGroups()?.map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup?.headers?.map((header) => {
                const isSortable =
                  header.column.columnDef.enableSorting !== false;
                return (
                  <th
                    key={header.id}
                    onClick={
                      isSortable ? () => handleSorting(header.id) : undefined
                    }
                    scope="col"
                    className="px-6 py-3"
                  >
                    <div className="flex items-center gap-1 cursor-pointer">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {sorting?.field === header.id &&
                        (sorting.order === 'desc' ? (
                          <PiSortAscendingBold className="text-[16px]" />
                        ) : (
                          <HiSortAscending className="text-[16px]" />
                        ))}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        {isListLoader && !isSortingLoader && (
          <SkeletonListing columnCount={columnCount} rowCount={rowCount} />
        )}
        <tbody className="bg-[#F3F3F3] dark:bg-slate-800 dark:text-slate-200">
          {table?.getRowModel()?.rows?.map((row) => (
            <tr
              key={row.id}
              onClick={() =>
                slug === 'projecListing'
                  ? pageNavigate(row.original.project_id || row.original.id)
                  : ''
              }
              className="bg-white border-b dark:bg-slate-900 dark:border-slate-700 mb-3"
            >
              {row?.getVisibleCells()?.map((cell) => {
                return (
                  <td
                    key={cell.id}
                    className="px-6 py-3 font-medium text-slate-900 whitespace-nowrap dark:text-white"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {isList && isList?.length > 0 && (
        <div className="flex justify-center items-center dark:text-white">
          <button
            disabled={pagination.currentPage === 1}
            onClick={() => handlePageChange(0)}
          >
            {'<<'}
          </button>
          <button
            disabled={pagination.currentPage === 1}
            onClick={() => handlePageChange(pagination.currentPage - 2)}
          >
            {'<'}
          </button>
          <span>
            Page <strong>{pagination.currentPage}</strong> of{' '}
            <strong>{pagination.totalPages}</strong>
          </span>
          <button
            disabled={pagination.currentPage === pagination.totalPages}
            onClick={() => handlePageChange(pagination.currentPage)}
          >
            {'>'}
          </button>
          <button
            disabled={pagination.currentPage === pagination.totalPages}
            onClick={() => handlePageChange(pagination.totalPages - 1)}
          >
            {'>>'}
          </button>
        </div>
      )}
    </>
  );
};

export default TableComponent;
